// src/pages/Login/Login.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import "./Login.css";
import AuthLayout from "../../components/AuthLayout/AuthLayout";
import GoogleAuthButton from "../../components/GoogleAuthButton/GoogleAuthButton";
import { clearAuthData, storeAuthData, getRedirectPath } from "../../utils/authSession";

// API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Email validation
const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Password validation
const validatePassword = (password) => {
  return password.length >= 8;
};

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // Handle input change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
    setSuccess("");
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!validateEmail(form.email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Validate password
    if (!validatePassword(form.password)) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
          remember_me: rememberMe,
        }),
      });

      // Parse response
      const data = await res.json();

      // Handle error responses
      if (!res.ok) {
        let errorMessage = "Login failed. Please try again.";

        switch (res.status) {
          case 400:
            errorMessage = data.detail || "Invalid email or password";
            break;
          case 401:
            errorMessage = "Invalid email or password";
            break;
          case 403:
            errorMessage = "Please verify your email before logging in";
            break;
          case 404:
            errorMessage = "Account not found. Please register first.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = data.detail || errorMessage;
        }

        throw new Error(errorMessage);
      }

      // Validate response data
      if (!data.data || !data.data.access_token) {
        throw new Error("Invalid response from server");
      }

      // Clear any existing auth data first
      clearAuthData();

      // Store new auth data
      const stored = storeAuthData(data.data, rememberMe);

      if (!stored) {
        throw new Error("Failed to store authentication data");
      }

      setSuccess("Login successful! Redirecting...");

      // Determine redirect path
      const redirectPath = getRedirectPath(data.data.role);

      console.log("Redirecting to:", redirectPath);

      // Small delay for success message visibility
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 500);
    } catch (err) {
      // Handle different error types
      if (err.name === "TypeError" && err.message.includes("fetch")) {
        setError(
          "Cannot connect to server. Please check your internet connection."
        );
      } else if (
        err.message.includes("NetworkError") ||
        err.message.includes("Failed to fetch")
      ) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(err.message);
      }

      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout heading="Welcome back to your learning.">
    <div className="login-container">
      <div className="login-card">
        <h2>Sign in</h2>
        <p className="auth-subtitle">Welcome back! Enter your details to continue.</p>

        {/* Error Banner */}
        {error && (
          <div className="error-banner">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Success Banner */}
        {success && (
          <div className="success-banner">
            <CheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              name="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              disabled={loading}
              required
              autoComplete="email"
              className={error && error.includes("email") ? "input-error" : ""}
            />
          </div>

          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <div className="password-input">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={handleChange}
              disabled={loading}
              required
              autoComplete="current-password"
              className={
                error && error.includes("password") ? "input-error" : ""
              }
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle"
              tabIndex="-1"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            </div>
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="forgot-password">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading || !form.email || !form.password}
            className={loading ? "loading" : ""}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <GoogleAuthButton remember={rememberMe} onError={setError} />

        <div className="form-footer">
          <p className="register-link">
            Don't have an account? <Link to="/register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
    </AuthLayout>
  );
};

export default Login;
